import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import type {
  DocumentTemplateEntityType,
  GetDocumentTypesForEntityType as GetDocumentTypesForEntityTypeData,
  GetDocumentTypesForEntityTypeVariables,
} from 'types/graphql';

const LoadDocumentTemplateState = gql`
  query LoadDocumentState($name: String!, $entityType: DocumentTemplateEntityType!) {
    documentTemplate(name: $name, entityType: $entityType) {
      id
      templateState
      showInBulkPrintMenu
    }
  }
`;

const FindDocumentTemplateId = gql`
  query FindDocumentTemplateId($name: String!, $entityType: DocumentTemplateEntityType!) {
    documentTemplate(name: $name, entityType: $entityType) {
      id
    }
  }
`;

const CreateDocumentTemplate = gql`
  mutation CreateDocumentTemplate($input: CreateDocumentTemplateInput!) {
    createDocumentTemplate(input: $input) {
      id
    }
  }
`;

const UpdateDocumentTemplate = gql`
  mutation UpdateDocumentTemplate($id: Int!, $input: UpdateDocumentTemplateInput!) {
    updateDocumentTemplate(id: $id, input: $input) {
      id
    }
  }
`;

/**
 * DocumentGenerator's template resolution: the one row `documentTemplates(take: 5000)[0]` used to pick, read as the
 * first row of `documentTemplatePage` in the same total order (updatedAt desc, then id desc) under the same filter.
 */
const GetDocumentTemplatesForEntityType = gql`
  query GetDocumentTemplatesForEntityType(
    $entityType: DocumentTemplateEntityType
    $types: [DocumentType!]
    $customDocumentNames: [String!]
  ) {
    documentTemplatePage(
      input: { first: 1, sortItems: [{ field: "updatedAt", sort: "desc" }] }
      entityType: $entityType
      types: $types
      customDocumentNames: $customDocumentNames
    ) {
      edges {
        node {
          id
          template
          name
          generationOptions
          type
        }
      }
    }
  }
`;

/** The side drawer's custom-template options: one page per entity, searched on the server as the user types. */
const GetCustomDocumentTemplatePage = gql`
  query GetCustomDocumentTemplatePage($entityType: DocumentTemplateEntityType, $input: StandardPageInput!) {
    documentTemplatePage(input: $input, entityType: $entityType, types: [CUSTOM]) {
      edges {
        node {
          id
          name
          generationOptions
          type
        }
      }
    }
  }
`;

const CUSTOM_TEMPLATE_PAGE_SIZE = 50;

/** The list root's order for the drawer's custom options (updatedAt desc, id desc); `searchText` narrows them. */
export const customTemplatePageInput = (searchText = '') => ({
  first: CUSTOM_TEMPLATE_PAGE_SIZE,
  sortItems: [{ field: 'updatedAt', sort: 'desc' }],
  ...(searchText ? { searchText } : {}),
});

/**
 * The note composer's templates for one entity type (P7-R35, FU-DT-BOUND), in one request:
 *
 *   documentTemplatePage ──► the attach menu's options: one page by name, searched on the server as the user types
 *   attached             ──► the templates the listed notes carry, read by id so each note names them (never capped)
 *
 * It replaces `documentTemplates(take: 5000)`, which read every template of the entity type for both.
 */
const GetDocumentTypesForEntityType = gql`
  query GetDocumentTypesForEntityType(
    $entityType: DocumentTemplateEntityType
    $input: StandardPageInput!
    $attachedInput: StandardPageInput!
    $withAttached: Boolean!
  ) {
    documentTemplatePage(input: $input, entityType: $entityType) {
      totalCount
      edges {
        node {
          id
          name
          generationOptions
          type
        }
      }
    }
    attached: documentTemplatePage(input: $attachedInput) @include(if: $withAttached) {
      edges {
        node {
          id
          name
          generationOptions
          type
        }
      }
    }
  }
`;

const NOTE_DOCUMENT_TEMPLATE_PAGE_SIZE = 50;

/** The composer's variables: options by name (`searchText` narrows them) plus the attached templates by id. */
const noteDocumentTemplateVariables = (
  entityType: DocumentTemplateEntityType | undefined,
  searchText: string,
  attachedIds: number[]
): GetDocumentTypesForEntityTypeVariables => {
  const ids = [...new Set(attachedIds)].sort((a, b) => a - b);
  return {
    entityType,
    input: {
      first: NOTE_DOCUMENT_TEMPLATE_PAGE_SIZE,
      sortItems: [{ field: 'name', sort: 'asc' }],
      ...(searchText ? { searchText } : {}),
    },
    attachedInput: { first: ids.length, gridFilters: [{ field: 'id', where: [{ in: ids }] }] },
    withAttached: ids.length > 0,
  };
};

type NoteTemplatePage = GetDocumentTypesForEntityTypeData['documentTemplatePage'] | null | undefined;
const templatesOf = (page: NoteTemplatePage) => (page?.edges ?? []).flatMap((edge) => (edge?.node ? [edge.node] : []));

/**
 * The custom document templates a note can attach to, for one entity type (the notes composer), in one request.
 * Skipped without an entity type. While a new search or attached set loads, the previous page stays on screen.
 *
 *   documentTemplates  ──► the attach menu's options (one page by name; `searchText` narrows it on the server)
 *   attachedTemplates  ──► those options plus the templates the notes carry, so every note names its templates
 *   totalCount         ──► the options' total: more than the page holds means the menu shows a search box
 */
export function useDocumentTypesForEntityType(
  entityType: DocumentTemplateEntityType | undefined,
  { searchText = '', attachedTemplateIds = [] }: { searchText?: string; attachedTemplateIds?: number[] } = {}
) {
  const { data, previousData, loading, error } = useQuery<
    GetDocumentTypesForEntityTypeData,
    GetDocumentTypesForEntityTypeVariables
  >(GetDocumentTypesForEntityType, {
    variables: noteDocumentTemplateVariables(entityType, searchText, attachedTemplateIds),
    skip: !entityType,
  });
  const templates = data ?? previousData;
  const documentTemplates = useMemo(
    () => (templates ? templatesOf(templates.documentTemplatePage) : undefined),
    [templates]
  );
  const attachedTemplates = useMemo(
    () => (templates ? [...(documentTemplates ?? []), ...templatesOf(templates.attached)] : undefined),
    [templates, documentTemplates]
  );
  return {
    documentTemplates,
    attachedTemplates,
    totalCount: templates?.documentTemplatePage.totalCount,
    loading,
    error,
  };
}

/** The document editor load modal: one page of the org's templates, newest first, searched and extended on the server. */
const GetDocumentTemplatesState = gql`
  query GetDocumentTemplatesState($input: StandardPageInput!) {
    documentTemplatePage(input: $input) {
      edges {
        node {
          id
          name
          templateState
          generationOptions
          entityType
          type
          showInBulkPrintMenu
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const LOAD_MODAL_TEMPLATE_PAGE_SIZE = 50;

/** The load modal's page input: the list root's order, an optional name search and `after` for "Show more". */
export const loadModalTemplatePageInput = (searchText = '', after?: string) => ({
  first: LOAD_MODAL_TEMPLATE_PAGE_SIZE,
  sortItems: [{ field: 'updatedAt', sort: 'desc' }],
  ...(searchText ? { searchText } : {}),
  ...(after ? { after } : {}),
});

export default {
  LoadDocumentTemplateState,
  FindDocumentTemplateId,
  CreateDocumentTemplate,
  UpdateDocumentTemplate,
  GetDocumentTemplatesState,
  GetDocumentTemplatesForEntityType,
  GetDocumentTypesForEntityType,
  GetCustomDocumentTemplatePage,
};
